import {
  Delete,
  Download,
  DriveFileRenameOutline,
  MoreVert,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Pagination,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminPatientDetailApi, instructionApi } from "apis";
import ConfirmationPopup from "components/common/ConfirmationPopup";
import RenameFileModal from "./RenameFileModal";
import { useToast } from "contexts/ToastContext";
import type {
  InstructionGenerationOutputDto,
  InstructionListResponseDto,
} from "libs/cellera-sdk";
import { useMemo, useState } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";
import {
  formatDateForFileName,
  formatDateForFileDisplay,
  formatDateTimeForDisplay,
  formatDayHeader,
  generateFileName,
  getFileIcon,
  handleFileDownload,
} from "utils/protocol-helpers";
import ShareButton from "components/common/ShareButton";
import { isShareSupported } from "utils/share";

interface AdminInstructionsProps {
  patientId: string;
  patientName?: string;
}

interface InstructionFile {
  type: "DOCX" | "PDF";
  url: string | undefined;
  fileName: string;
}

interface Instruction {
  id: string;
  date: Date;
  displayLabel: string;
  files: InstructionFile[];
  isHiddenFromPatient: boolean;
  customName?: string;
}

interface DayGroup {
  dayKey: string;
  date: Date;
  instructions: Instruction[];
}

const PAGE_SIZE = 25;

const AdminInstructions: React.FC<AdminInstructionsProps> = ({
  patientId,
  patientName,
}) => {
  const { colors } = useCelleraTheme();
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [renameTarget, setRenameTarget] = useState<{
    id: string;
    currentName: string;
  } | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<{
    el: HTMLElement;
    id: string;
    name: string;
  } | null>(null);
  const [pendingVisibilityId, setPendingVisibilityId] = useState<string | null>(
    null,
  );
  const [toggleTarget, setToggleTarget] = useState<{
    instructionId: string;
    nextIsHidden: boolean;
  } | null>(null);

  // NOTE: Uses the provider-facing instructionApi intentionally — no admin-specific
  // instruction fetch endpoint exists in the SDK. This works because the backend
  // instruction controller has a role-bypass that accepts admin tokens (added in
  // backend PR #560). If a dedicated admin SDK method is added in the future,
  // replace this with adminPatientDetailApi equivalent.
  const { data: instructionsData, isLoading } =
    useQuery<InstructionListResponseDto>({
      queryKey: ["admin-instructionsByPatient", patientId, page],
      queryFn: () =>
        instructionApi.getInstructionByPatientId({
          patientId,
          page,
          pageSize: PAGE_SIZE,
        }),
      enabled: !!patientId,
    });

  const deleteMutation = useMutation({
    mutationFn: (instructionId: string) =>
      adminPatientDetailApi.deleteInstructionForAdmin({
        instructionId,
        patientId,
      }),
    onError: () => {
      showError("Failed to delete instruction. Please try again.");
    },
  });

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget);
      setDeleteTarget(null);
      await queryClient.invalidateQueries({
        queryKey: ["admin-instructionsByPatient", patientId],
      });
      showSuccess("Instruction deleted successfully.");
    } catch {
      // onError handles the toast
    }
  };

  const visibilityMutation = useMutation({
    mutationFn: ({
      instructionId,
      isHiddenFromPatient,
    }: {
      instructionId: string;
      isHiddenFromPatient: boolean;
    }) =>
      adminPatientDetailApi.updateInstructionVisibilityForAdmin({
        patientId,
        instructionId,
        updateInstructionVisibilityDto: { isHiddenFromPatient },
      }),
    onMutate: (variables) => {
      setPendingVisibilityId(variables.instructionId);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["admin-instructionsByPatient", patientId],
      });
      showSuccess(
        variables.isHiddenFromPatient
          ? "This document is hidden from the patient"
          : "This document is visible for the patient",
      );
    },
    onError: () => {
      showError("Failed to update visibility. Please try again.");
    },
    onSettled: () => {
      setPendingVisibilityId(null);
      setToggleTarget(null);
    },
  });

  const handleToggleClick = (
    instructionId: string,
    currentIsHidden: boolean,
  ) => {
    setToggleTarget({ instructionId, nextIsHidden: !currentIsHidden });
  };

  const handleToggleConfirm = () => {
    if (!toggleTarget) return;
    visibilityMutation.mutate({
      instructionId: toggleTarget.instructionId,
      isHiddenFromPatient: toggleTarget.nextIsHidden,
    });
  };

  const renameMutation = useMutation({
    mutationFn: ({
      instructionId,
      customName,
    }: {
      instructionId: string;
      customName: string;
    }) =>
      adminPatientDetailApi.renameInstructionForAdmin({
        patientId,
        instructionId,
        renameInstructionDto: { customName },
      }),
    onSuccess: () => {
      setRenameTarget(null);
      queryClient.invalidateQueries({
        queryKey: ["admin-instructionsByPatient", patientId],
      });
      showSuccess("Update successful");
    },
    onError: () => {
      showError("Failed to rename. Please try again.");
    },
  });

  const nameAbbreviation = useMemo(() => {
    if (!patientName) return "XX";
    const parts = patientName.trim().split(/\s+/);
    if (parts.length >= 2) {
      return `${parts[0].charAt(0).toUpperCase()}${parts[parts.length - 1].charAt(0).toUpperCase()}`;
    }
    return parts[0].charAt(0).toUpperCase() + "X";
  }, [patientName]);

  const instructions = useMemo<Instruction[]>(() => {
    const abbr = nameAbbreviation;
    const seenFileNames = new Map<string, number>();

    const getUniqueFileName = (
      abbr: string,
      dateStr: string,
      ext: "docx" | "pdf",
    ): string => {
      const base = generateFileName(abbr, dateStr, ext);
      const count = seenFileNames.get(base) ?? 0;
      seenFileNames.set(base, count + 1);
      return count === 0 ? base : generateFileName(abbr, dateStr, ext, count);
    };

    return (instructionsData?.instructions ?? []).map(
      (item: InstructionGenerationOutputDto) => {
        const dateStr = formatDateForFileName(item.createdAt);
        const date = new Date(item.createdAt);
        const files: InstructionFile[] = [];

        if (item.instructionTxtUrl) {
          files.push({
            type: "DOCX",
            url: item.instructionTxtUrl as unknown as string,
            fileName: getUniqueFileName(abbr, dateStr, "docx"),
          });
        }
        if (item.instructionPdfUrl) {
          files.push({
            type: "PDF",
            url: item.instructionPdfUrl as unknown as string,
            fileName: getUniqueFileName(abbr, dateStr, "pdf"),
          });
        }

        return {
          id: item.instructionId,
          date,
          displayLabel: item.customName
            ? String(item.customName)
            : `${abbr}${dateStr}`,
          files,
          isHiddenFromPatient: item.isHiddenFromPatient ?? false,
          customName: item.customName ? String(item.customName) : undefined,
        };
      },
    );
  }, [instructionsData, nameAbbreviation]);

  const instructionsByDay = useMemo<DayGroup[]>(() => {
    const map = new Map<string, DayGroup>();
    instructions.forEach((item) => {
      const key = item.date.toDateString();
      if (!map.has(key)) {
        map.set(key, {
          dayKey: key,
          date: item.date,
          instructions: [],
        });
      }
      map.get(key)!.instructions.push(item);
    });
    return Array.from(map.values()).sort(
      (a, b) => b.date.getTime() - a.date.getTime(),
    );
  }, [instructions]);

  const totalPages = useMemo(() => {
    const total = instructionsData?.pagination?.total ?? 0;
    return Math.ceil(total / PAGE_SIZE);
  }, [instructionsData]);

  const renderFileCard = (file: InstructionFile, date: Date) => {
    const IconComponent = getFileIcon(file.type);
    const displayDate = formatDateForFileDisplay(date);
    const iconColor = file.type === "PDF" ? "#e53935" : colors.primary.main;

    return (
      <Box
        key={file.fileName}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2,
          border: `1px solid ${colors.border.light}`,
          borderRadius: "8px",
          flex: 1,
          minWidth: 200,
        }}
      >
        <IconComponent sx={{ color: iconColor, fontSize: 36, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: colors.text.primary }}
            noWrap
          >
            {file.fileName}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {displayDate}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {file.type} Format
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            sx={{
              minWidth: 0,
              p: 0.5,
              color: colors.primary.main,
              flexShrink: 0,
            }}
            onClick={() => handleFileDownload(file.url, file.fileName)}
          >
            <Download />
          </Button>
          {isShareSupported && file.url && (
            <ShareButton
              url={file.url}
              fileName={file.fileName}
              color={colors.primary.main}
            />
          )}
        </Box>
      </Box>
    );
  };

  const renderInstructionCard = (item: Instruction, index: number) => {
    const displayDateTime = formatDateTimeForDisplay(item.date);

    return (
      <Card
        key={`instruction-${item.id}-${index}`}
        sx={{
          mb: 2,
          border: `1px solid ${colors.border.light}`,
          borderRadius: "8px",
          "&:hover": { boxShadow: 2 },
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: colors.text.primary }}
              >
                {item.displayLabel}
              </Typography>
              <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                Created on {displayDateTime}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={(e) =>
                setMenuAnchor({
                  el: e.currentTarget,
                  id: item.id,
                  name: item.displayLabel,
                })
              }
            >
              <MoreVert />
            </IconButton>
          </Box>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {item.files.map((file) => renderFileCard(file, item.date))}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
            <Checkbox
              checked={!item.isHiddenFromPatient}
              disabled={pendingVisibilityId === item.id}
              onChange={() =>
                handleToggleClick(item.id, item.isHiddenFromPatient)
              }
              sx={{
                p: 0,
                mr: 1,
                color: colors.text.secondary,
                "&.Mui-checked": { color: colors.primary.main },
              }}
            />
            <Typography variant="body2" sx={{ color: colors.text.primary }}>
              The patient can see this document
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (instructions.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 200,
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: colors.text.secondary, fontStyle: "italic" }}
        >
          No instructions found for this patient
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {instructionsByDay.map((dayGroup, dayIndex) => (
          <Box key={dayGroup.dayKey}>
            <Box
              sx={{
                mb: 3,
                pb: 2,
                borderBottom: `2px solid ${colors.border.main}`,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: colors.text.primary,
                  fontSize: "1.5rem",
                }}
              >
                {formatDayHeader(dayGroup.date)}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: colors.text.secondary, mt: 0.5 }}
              >
                {dayGroup.instructions.length}{" "}
                {dayGroup.instructions.length === 1
                  ? "instruction"
                  : "instructions"}{" "}
                generated
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >
              {dayGroup.instructions.map((item, index) =>
                renderInstructionCard(item, index),
              )}
            </Box>
            {dayIndex < instructionsByDay.length - 1 && (
              <Divider
                sx={{
                  mt: 4,
                  mb: 2,
                  borderColor: colors.border.light,
                }}
              />
            )}
          </Box>
        ))}
      </Box>

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      <ConfirmationPopup
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title="Delete Instruction"
        message="Are you sure you want to delete this instruction for the patient? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="#d32f2f"
        loading={deleteMutation.isPending}
        maxWidth="sm"
        paperSx={{ borderRadius: 4 }}
      />

      <Menu
        anchorEl={menuAnchor?.el}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            if (menuAnchor) {
              setRenameTarget({
                id: menuAnchor.id,
                currentName: menuAnchor.name,
              });
            }
            setMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <DriveFileRenameOutline fontSize="small" />
          </ListItemIcon>
          <ListItemText>Rename document</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuAnchor) {
              setDeleteTarget(menuAnchor.id);
            }
            setMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete document</ListItemText>
        </MenuItem>
      </Menu>

      <RenameFileModal
        open={!!renameTarget}
        currentName={renameTarget?.currentName ?? ""}
        onClose={() => setRenameTarget(null)}
        onConfirm={(newName) =>
          renameTarget &&
          renameMutation.mutate({
            instructionId: renameTarget.id,
            customName: newName,
          })
        }
        loading={renameMutation.isPending}
      />

      <ConfirmationPopup
        open={!!toggleTarget}
        onClose={() => setToggleTarget(null)}
        onConfirm={handleToggleConfirm}
        title={
          toggleTarget?.nextIsHidden
            ? "Hide from Patient"
            : "Make Visible to Patient"
        }
        message={
          toggleTarget?.nextIsHidden
            ? "Are you sure you want to hide this document from the patient? The patient will no longer be able to see this document."
            : "Are you sure you want to make this document visible to the patient? The patient will be able to see this document."
        }
        confirmText={toggleTarget?.nextIsHidden ? "Hide" : "Make Visible"}
        cancelText="Cancel"
        confirmColor={
          toggleTarget?.nextIsHidden ? colors.warning[600] : colors.primary.main
        }
        loading={visibilityMutation.isPending}
        maxWidth="sm"
        paperSx={{ borderRadius: 4 }}
      />
    </Box>
  );
};

export default AdminInstructions;
